import { isArray } from '@ember/array';
import { get } from '@ember/object';
import { intersection } from '@square/dashboard-utilities/utils/intersection';
// returns true if firstArray and secondArray have same elements (any order)
export function sameElements(firstArray, secondArray) {
  if (!firstArray) {
    // eslint-disable-next-line no-param-reassign
    firstArray = [];
  }
  if (!secondArray) {
    // eslint-disable-next-line no-param-reassign
    secondArray = [];
  }
  if (firstArray === secondArray) {
    return true;
  } else if (firstArray.length !== secondArray.length) {
    return false;
  } else {
    return intersection(firstArray, secondArray).length === firstArray.length;
  }
}
export function equal(firstArray, secondArray) {
  if (!Array.isArray(firstArray) || !Array.isArray(secondArray)) {
    return false;
  }
  if (firstArray.length !== secondArray.length) {
    return false;
  }
  for (const [i, element] of firstArray.entries()) {
    if (element !== secondArray[i]) {
      return false;
    }
  }
  return true;
}
export function shuffleArray(a) {
  if (!a) {
    return null;
  }
  for (let i = a.length; i; i--) {
    const j = Math.floor(Math.random() * i);
    // eslint-disable-next-line no-param-reassign
    [a[i - 1], a[j]] = [a[j], a[i - 1]];
  }
  return a;
}
export function asArray(object) {
  if (!object) {
    return [];
  } else if (isArray(object)) {
    return object;
  }
  return [object];
}
function group(array, func) {
  return array.reduce(function (memo, item) {
    const itemKey = func(item);
    if (!memo[itemKey]) {
      // eslint-disable-next-line no-param-reassign
      memo[itemKey] = [];
    }
    memo[itemKey].push(item);
    return memo;
  }, {});
}
export function groupBy(array, key) {
  return group(array, item => get(item, key)); // This is guaranteed to be a string because of KeyForGetValue.
}

/**
 * Takes an array and a predicate, and returns a two-element array of arrays that do and
 * do not match the predicate, respectively.
 * @param {Array} array An array of elements to filter.
 * @param {Function} predicate A predicate to determine which side the element belongs to.
 * @returns {Array} A two-element array, where the first element is an array of elements that match the predicate, and the
 * second the elements that do not match the predicate.
 */
export function partition(array, predicate) {
  const accepted = array.filter(predicate);
  const rejected = array.filter(element => !predicate(element));
  return [accepted, rejected];
}

/**
 * Takes an array and optional value mapper, and returns duplicate items
 *
 * @param {Array} array - An array to filter for duplicates
 * @param  {Function} [mapValue] - A function that maps an array item to a value for duplicate comparison.
 * Uses the identity function by default
 * @returns {Array} - The subset of items in the array which are duplicates
 */
export function duplicates(array, mapValue = item => item) {
  const seen = new Set();
  const dups = new Set();
  for (const item of array) {
    const itemValue = mapValue(item);
    if (seen.has(itemValue)) {
      dups.add(item);
    }
    seen.add(itemValue);
  }
  return [...dups];
}